import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import AnimateOnScroll from './AnimateOnScroll.js';
import PageHero from './PageHero.js';
import regionKootenays from '../assets/region-kootenays.jpg';

const KootenayPage = () => {
  return (
    <div className="bg-custom-earth">
      <div className="wood-container">
        <div className="min-h-screen flex flex-col">
          <Helmet>
            <title>Vegetation Management Kootenays BC | Boultbee</title>
            <meta name="description" content="Industrial and municipal herbicide application in Trail, Castlegar, Nelson, Invermere, and the Kootenays. Mining, pulp and paper, and municipal vegetation management." />
            <link rel="canonical" href="https://www.weedmanager.com/service-areas/kootenays" />
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content="Boultbee Vegetation Management" />
            <meta property="og:title" content="Vegetation Management Kootenays BC | Boultbee" />
            <meta property="og:description" content="Industrial and municipal herbicide application in Trail, Castlegar, Nelson, Invermere, and the Kootenays. Mining, pulp and paper, and municipal vegetation management." />
            <meta property="og:url" content="https://www.weedmanager.com/service-areas/kootenays" />
            <meta property="og:image" content="https://www.weedmanager.com/images/og-kootenays.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Vegetation Management Kootenays BC | Boultbee" />
            <meta name="twitter:description" content="Industrial and municipal herbicide application in Trail, Castlegar, Nelson, Invermere, and the Kootenays. Mining, pulp and paper, and municipal vegetation management." />
            <meta name="twitter:image" content="https://www.weedmanager.com/images/og-kootenays.jpg" />
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.weedmanager.com/" },
                  { "@type": "ListItem", "position": 2, "name": "Kootenays & Columbia Valley", "item": "https://www.weedmanager.com/service-areas/kootenays" }
                ]
              })}
            </script>
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Service",
                "serviceType": "Industrial Vegetation Management",
                "provider": {
                  "@type": "LocalBusiness",
                  "name": "Boultbee Vegetation Management",
                  "url": "https://www.weedmanager.com"
                },
                "areaServed": [
                  { "@type": "City", "name": "Trail" },
                  { "@type": "City", "name": "Castlegar" },
                  { "@type": "City", "name": "Nelson" },
                  { "@type": "City", "name": "Invermere" },
                  { "@type": "City", "name": "Cranbrook" },
                  { "@type": "City", "name": "Rossland" },
                  { "@type": "City", "name": "Fernie" }
                ],
                "description": "Industrial and municipal herbicide application in the Kootenays including Trail, Castlegar, Nelson, Invermere, and the Columbia Valley."
              })}
            </script>
          </Helmet>

          <PageHero
            title="Kootenays & Columbia Valley"
            subtitle="Industrial and municipal herbicide application across the Kootenays"
            backgroundImage={regionKootenays}
            bgPosition="center bottom"
          />

          <AnimateOnScroll animation="fade-in-up">
            <p className="text-center text-lg md:text-xl font-serif italic text-green-900 max-w-3xl mx-auto px-6 py-6">Boultbee makes yearly service runs through the Kootenays, providing vegetation management to industrial, mining, and municipal clients across the region.</p>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Mining & Industrial</h2>
              <p className="text-gray-700">The Kootenays have a long history in mining and heavy industry, and these operations require professional vegetation management to maintain safe, compliant sites. Boultbee provides bare ground control for mine sites, industrial yards, and associated infrastructure in the Trail, Rossland, and Castlegar area.</p>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Pulp & Paper and Forestry</h2>
              <p className="text-gray-700">The region's forestry sector generates steady demand for vegetation management around mill yards, log sorts, and processing facilities. Boultbee provides bare ground herbicide programs that keep operational areas clear and safe. We also treat associated rail sidings and loading areas that service these facilities.</p>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Municipal & Utility Services</h2>
              <p className="text-gray-700">Kootenay municipalities rely on Boultbee for sidewalk weed control, park turf programs, and noxious weed management. We also maintain bare ground conditions at utility substations and other infrastructure throughout the region. Our yearly Kootenay route allows us to coordinate scheduling efficiently across multiple communities from Nelson and Castlegar through to Invermere and the Columbia Valley.</p>
            </div>
          </AnimateOnScroll>

          {/* Other Service Areas */}
          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 text-center">Other Regions We Serve</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Link to="/service-areas/okanagan" className="block bg-white/80 rounded-lg p-4 text-center hover-lift border border-gray-200">
                  <svg className="w-6 h-6 text-green-700 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" /></svg>
                  <h3 className="font-bold text-gray-900">Okanagan</h3>
                  <p className="text-gray-700 text-sm mt-1">Penticton, Kelowna & Vernon</p>
                </Link>
                <Link to="/service-areas/interior-bc" className="block bg-white/80 rounded-lg p-4 text-center hover-lift border border-gray-200">
                  <svg className="w-6 h-6 text-green-700 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" /></svg>
                  <h3 className="font-bold text-gray-900">Interior BC</h3>
                  <p className="text-gray-700 text-sm mt-1">Kamloops, Merritt & Revelstoke</p>
                </Link>
                <Link to="/service-areas/lower-mainland" className="block bg-white/80 rounded-lg p-4 text-center hover-lift border border-gray-200">
                  <svg className="w-6 h-6 text-green-700 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" /></svg>
                  <h3 className="font-bold text-gray-900">Lower Mainland</h3>
                  <p className="text-gray-700 text-sm mt-1">Surrey, Langley & Fraser Valley</p>
                </Link>
              </div>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur text-center">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Get a Quote</h2>
              <p className="text-gray-700 mb-5">Serving the Kootenays on our yearly route from our Okanagan base. <Link to="/services" className="text-green-700 underline hover:text-green-900">View all services</Link> or contact us for a site assessment.</p>
              <Link to="/contact" className="cta-button">
                Get a Quote
              </Link>
            </div>
          </AnimateOnScroll>
        </div>
      </div>
    </div>
  );
};

export default KootenayPage;
